import { getPreviewHTMLTemplateUrl } from 'modules/campaigns/lib/utils/template';
import {
  EmailContentDefinition,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import styled from 'styled-components';
import { OverflowMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { BUTTON_SIZES } from 'ui/button';
import { OpenNewWindow24 } from 'ui/icons';
import { spacing } from 'ui/theme';
import { BottomTooltip } from 'ui/tooltip';
import { ContentModalStateType, ContentModals, useModalContext } from 'utils/contexts/modal';
import { WhatsApp } from 'constants/messageMediums';
import {
  getCampaignEditorLinkWithExperimentTakeover,
  getCreateExperimentLink,
} from 'utils/appRoutes';
import { useCampaignQueryContext } from '../../context/CampaignQueryContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useTemplateQueryContext } from '../../context/TemplateQueryContext';
import { PREVIEW_IN_DEVICE_URL, REVISION_HISTORY_URL, SPAM_REPORT_HREF } from '../constants';
import { displayContent } from '../content';
import { MenuOptionNames, MessagingPermissionOptions, TemplateEditViews } from '../types';
import {
  downloadHtml,
  getDoesCampaignHaveExperiments,
  useGetCanManageBaseTemplates,
  useGetMessagingPermission,
} from '../utils';
import { useSelector } from 'react-redux';
import { selectIsReactSpamReportFlagOn } from 'core/userContext';
import {
  ModalType as TemplateEditorModalType,
  useModalContext as useTemplateEditorModalContext,
} from 'modules/templateEditor/context';

const TooltipTrigger = styled.div`
  display: flex;
  align-items: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${spacing.half};
`;

export const OverflowButton = () => {
  const isReactSpamReportFFOn = useSelector(selectIsReactSpamReportFlagOn);
  const { medium } = useTemplateQueryContext();
  const { isDirty, hasContent, liveTemplate } = useEditableFieldsContext();
  const { campaign } = useCampaignQueryContext();
  const {
    campaignId,
    experimentFlow: isExperimentFlow,
    locale,
    templateId,
    view,
  } = useQueryParamContext();
  const { showModal } = useModalContext<ContentModalStateType>();
  const { openModal } = useTemplateEditorModalContext();
  const contentData = (liveTemplate?.mediumData || {}) as
    | EmailContentDefinition
    | InAppContentDefinition;
  const showEditExperiments = getDoesCampaignHaveExperiments(campaign);
  const isCampaignFlow = !!campaignId;
  const messagingPermission = useGetMessagingPermission();
  const canManageBaseTemplates = useGetCanManageBaseTemplates();
  const isCopyOptionDisabled =
    isCampaignFlow || isExperimentFlow
      ? [MessagingPermissionOptions.NONE, MessagingPermissionOptions.VIEW_ONLY].includes(
          messagingPermission,
        )
      : !canManageBaseTemplates;
  const copyOptionTooltipContent =
    isCampaignFlow || isExperimentFlow
      ? displayContent.tooltipViewOnlyPermission
      : displayContent.tooltipManageBaseTemplatesPermission;
  const isABExperimentOptionDisabled = [
    MessagingPermissionOptions.NONE,
    MessagingPermissionOptions.VIEW_ONLY,
  ].includes(messagingPermission);
  const isDesignOrPreviewMode =
    view === TemplateEditViews.preview || view === TemplateEditViews.content;

  const overflowOptions: { value: ActionMenuOption; show: boolean }[] = [
    {
      value: {
        id: 'create-ab-experiment-option',
        as: AllowedActionMenuElements.Link,
        isDisabled: isABExperimentOptionDisabled,
        content: (
          <>
            <BottomTooltip
              isEnabled={isABExperimentOptionDisabled}
              tooltipContent={displayContent.tooltipViewOnlyPermission}
            >
              {showEditExperiments
                ? MenuOptionNames.EDIT_A_B_EXPERIMENT
                : MenuOptionNames.CREATE_A_B_EXPERIMENT}
            </BottomTooltip>
          </>
        ),
        href: `${
          showEditExperiments
            ? getCampaignEditorLinkWithExperimentTakeover(campaignId, campaign?.experiments[0]?.id)
            : getCreateExperimentLink(campaignId)
        }`,
      },
      show: isCampaignFlow, // experiment flow is a subset of campaign flow
    },
    {
      value: {
        id: 'preview-in-new-device',
        as: AllowedActionMenuElements.Link,
        content: (
          <ContentWrapper>
            {MenuOptionNames.PREVIEW_IN_DEVICE}
            <OpenNewWindow24 />
          </ContentWrapper>
        ),
        href: `${PREVIEW_IN_DEVICE_URL}?templateId=${templateId}`,
        isExternalLink: true,
      },
      show: isDesignOrPreviewMode && medium === MessageMedium.Email,
    },
    {
      value: {
        id: 'preview-in-new-tab-option',
        as: AllowedActionMenuElements.Link,
        content: (
          <ContentWrapper>
            {MenuOptionNames.PREVIEW_IN_NEW_TAB}
            <OpenNewWindow24 />
          </ContentWrapper>
        ),
        href: getPreviewHTMLTemplateUrl(templateId, locale),
        isExternalLink: true,
      },
      show:
        isDesignOrPreviewMode && (medium === MessageMedium.Email || medium === MessageMedium.InApp),
    },
    {
      value: {
        id: 'save-a-template-option',
        as: AllowedActionMenuElements.Button,
        isDisabled: isCopyOptionDisabled,
        content: (
          <>
            <BottomTooltip
              isEnabled={isCopyOptionDisabled}
              tooltipContent={copyOptionTooltipContent}
            >
              <TooltipTrigger>{MenuOptionNames.SAVE_AS_NEW_TEMPLATE}</TooltipTrigger>
            </BottomTooltip>
          </>
        ),
        onClick: () => showModal({ modal: ContentModals.CopyTemplate, showCopyModal: 'show' }),
      },
      show: medium !== WhatsApp,
    },
    {
      value: {
        id: 'export-html-option',
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            {/* icon goes here */}
            <span>{MenuOptionNames.EXPORT_HTML}</span>
          </>
        ),
        onClick: () => downloadHtml(`template-${templateId}.html`, contentData.html),
      },
      show: medium === MessageMedium.Email || medium === MessageMedium.InApp,
    },
    {
      value: {
        id: 'revision-history-option',
        as: AllowedActionMenuElements.Link,
        content: (
          <ContentWrapper>
            {MenuOptionNames.REVISION_HISTORY}
            <OpenNewWindow24 />
          </ContentWrapper>
        ),
        href: `${REVISION_HISTORY_URL}?templateId=${templateId}`,
        isExternalLink: true,
      },
      show: isDesignOrPreviewMode,
    },
    {
      value: {
        id: 'spam-report-option',
        as: isReactSpamReportFFOn
          ? AllowedActionMenuElements.Button
          : AllowedActionMenuElements.Link,
        content: (
          <ContentWrapper>
            {MenuOptionNames.SPAM_REPORT}
            {!isReactSpamReportFFOn && <OpenNewWindow24 />}
          </ContentWrapper>
        ),
        ...(isReactSpamReportFFOn
          ? { onClick: () => openModal(TemplateEditorModalType.SpamReport) }
          : {
              href: `${SPAM_REPORT_HREF}?templateId=${templateId}`,
              isExternalLink: true,
            }),
      },
      show: medium === MessageMedium.Email,
    },
  ];

  const filteredOptions = overflowOptions.filter((o) => o.show).map(({ value }) => value);

  return (
    <>
      {filteredOptions?.length > 0 && (
        <OverflowMenu
          id={`template-editor-overflow`}
          data-test="template-editor-overflow"
          isDisabled={isDirty || !hasContent}
          options={filteredOptions}
          size={BUTTON_SIZES.LARGE}
        />
      )}
    </>
  );
};

export default OverflowButton;
